// Cliente HTTP do React: instância axios dedicada que espelha o AuthInterceptor
// do Angular (headers de auth/contexto + tratamento básico de falha de auth).
// Acoplamento restrito por design (Req 7.3): apenas api-config.ts, auth-storage.ts
// e globais do browser — nenhum service/classe/módulo Angular.
//
// Esqueleto (Tarefa 2.1): os símbolos importados são consumidos nas etapas
// seguintes — request interceptor e helpers (2.2–2.4), response interceptor (3.x)
// e o wiring da instância via axios.create + export default (4).
import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';

import { API_BASE_URL } from './api-config';
import { getAccessToken, getTenantHeader, getLocale, getTimeZone } from './auth-storage';

// Espelha o AuthInterceptor: a troca de token (refresh) não deve receber o header
// Authorization. O fluxo de refresh envia o corpo urlencoded; coagimos qualquer
// valor a string defensivamente para nunca lançar com corpos não previstos.
function isRefreshTokenRequest(config: InternalAxiosRequestConfig): boolean {
  const body = config.data;
  if (body == null) {
    return false;
  }
  const asText = typeof body === 'string' ? body : String(body);
  return asText.includes('&grant_type=refresh_token');
}

// Espelha o bloco de headers do AuthInterceptor. AxiosHeaders.from garante a
// normalização case-insensitive do nome do header, equivalente ao HttpHeaders.set
// do Angular. O x-origin do Angular é omitido por design (Req 3.7): ele é acoplado
// à página de correspondência (router.url) e não tem equivalente no React.
function applyAuthHeaders(config: InternalAxiosRequestConfig): void {
  const headers = AxiosHeaders.from(config.headers);

  const token = getAccessToken();
  // Não autentica a troca de token nem sobrescreve um Authorization já definido.
  if (token && !isRefreshTokenRequest(config) && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const tenant = getTenantHeader();
  if (tenant) {
    headers.set('tenant', tenant);
  }

  // locale sempre presente: getLocale() nunca retorna vazio (fallback pt_BR na Task 1).
  headers.set('locale', getLocale());

  const timezone = getTimeZone();
  if (timezone) {
    headers.set('timezone', timezone);
  }

  config.headers = headers;
}

// Request interceptor da instância (Req 1.3): delega a montagem dos headers a
// applyAuthHeaders, que já reatribui config.headers. A degradação graciosa fica
// garantida pela Task 1 (auth-storage nunca lança), então nenhum try/catch é
// necessário aqui — o interceptor sempre devolve uma config válida.
function requestInterceptor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  applyAuthHeaders(config);
  return config;
}

// Origem na urlApi: a instância usa baseURL, então a URL absoluta da resposta pode
// estar em error.config.baseURL/url (path relativo resolvido) ou em
// error.request.responseURL. A checagem é mantida para fidelidade ao AuthInterceptor
// e para blindar contra chamadas com URL absoluta a outro host. Optional chaining +
// fallback para string vazia evitam lançar quando config/request faltam (erro de rede).
function isFromApiBaseUrl(error: AxiosError): boolean {
  const base = API_BASE_URL;
  const cfg = error.config;
  const composed = `${cfg?.baseURL ?? ''}${cfg?.url ?? ''}`;
  const responseURL = (error.request as { responseURL?: string } | undefined)?.responseURL ?? '';
  return composed.includes(base) || responseURL.includes(base);
}

// Auth-failure (Req 4.1/4.2/4.4): espelha o AuthInterceptor, que redireciona em
// 401/403 da urlApi OU quando o corpo do erro indica missingTenant. Sem response
// (rede/timeout/cancelamento) o status é undefined, então não é auth-failure; e
// 401/403 de outro host sem missingTenant cai no pass-through. O cast defensivo
// do corpo evita acoplar a um tipo de erro da API que não pertence a esta task.
function isAuthFailure(error: AxiosError): boolean {
  const status = error.response?.status;
  const is401or403 = status === 401 || status === 403;

  const body = error.response?.data as { missingTenant?: boolean } | undefined;
  const missingTenant = Boolean(body?.missingTenant);

  return (is401or403 && isFromApiBaseUrl(error)) || missingTenant;
}

// Espelha DesignSystemSigmaService.isDssLayout(): o Angular detecta o contexto DSS
// pela presença de '/dss/' no path corrente. Com useHash:true, esse path vive no
// window.location.hash, que é string vazia na rota raiz.
function isDssLayout(): boolean {
  return window.location.hash.includes('/dss/');
}

// Navegação por hash (HashRouter), nunca o Angular Router (Req 7.4): reproduz o
// destino que o AuthInterceptor já usa hoje — rota de erro DSS quando o layout é
// DSS, senão o login do Angular. As exceções de rota do Angular (list-cameras,
// pages/softphone, list-correspondencias-int) não são replicadas (Req 4.7): guardam
// páginas Angular e não se aplicam às telas React. O reset de sessão permanece no
// login Angular — esta camada é read-only sobre o storage (Req 4.6).
function redirectToAuthRoute(): void {
  window.location.hash = isDssLayout() ? '#/pages/error/dss' : '#/pages/login';
}

// Response interceptor (onRejected): em auth-failure (401/403 da urlApi ou
// missingTenant) o Angular redireciona ao login/DSS; reproduzimos só esse redirect.
// 428/precondition, toast genérico centralizado e o fluxo DSS completo (token via
// postMessage) ficam fora desta task (Task 6). Em qualquer ramo rejeitamos com o
// erro original (Req 4.5/5.1): o auth-failure não deixa o chamador pendurado e o
// erro não-auth sobe intacto para a camada de dados/tela tratar.
function onResponseError(error: AxiosError): Promise<never> {
  if (isAuthFailure(error)) {
    redirectToAuthRoute();
  }
  return Promise.reject(error);
}

// Instância dedicada do React (Req 1.1/1.4): baseURL vem exclusivamente de
// API_BASE_URL (api-config.ts, Task 1), nunca de um literal. É criada uma única vez
// no módulo (singleton) e exportada como default para a Task 3 reusar a mesma
// configuração — sem novo axios.create.
const axiosInstance = axios.create({ baseURL: API_BASE_URL });

// Exatamente um request e um response interceptor (Req 1.3). No response, o handler
// de sucesso devolve a 2xx inalterada (Req 5.5) e onResponseError cuida do
// auth-failure (redirect) ou do pass-through dos demais erros.
axiosInstance.interceptors.request.use(requestInterceptor);
axiosInstance.interceptors.response.use((response) => response, onResponseError);

export default axiosInstance;
